import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaEdit, FaPlus, FaTrash, FaImage, FaSave, FaTimes,
  FaGlobe, FaMapMarkerAlt, FaPhone, FaEnvelope, FaLinkedin,
  FaTwitter, FaFacebook, FaInstagram, FaYoutube, FaCheckCircle
} from 'react-icons/fa';
import { getFileUrl } from '../utils/fileUtils';
import API from '../utils/api';
import { toast } from 'react-toastify';

const ManageCompany = () => {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [editing, setEditing] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [newBenefit, setNewBenefit] = useState('');

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website: '',
    industry: '',
    companySize: '',
    founded: '',
    specialties: [],
    benefits: [],
    cultureTags: [],
    location: { city: '', state: '', country: '', address: '' },
    socialLinks: { linkedin: '', twitter: '', facebook: '', instagram: '', youtube: '' }
  });

  useEffect(() => {
    fetchCompany();
  }, []);

  const fetchCompany = async () => {
    try {
      const { data } = await API.get('/companies/my-company');
      setCompany(data);
      
      // Fetch gallery images for this company
      if (data._id) {
        fetchGalleryImages(data._id);
      }
      
      setFormData({
        name: data.name || '',
        description: data.description || '',
        website: data.website || '',
        industry: data.industry || '',
        companySize: data.companySize || '',
        founded: data.founded || '',
        specialties: data.specialties || [],
        benefits: data.benefits || [],
        cultureTags: data.cultureTags || [],
        location: data.location || { city: '', state: '', country: '', address: '' },
        socialLinks: data.socialLinks || { linkedin: '', twitter: '', facebook: '', instagram: '', youtube: '' }
      });
    } catch (error) {
      toast.error('Failed to load company data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGalleryImages = async (companyId) => {
    try {
      setGalleryLoading(true);
      const { data } = await API.get(`/gallery/company/${companyId}`);
      if (data.data && Array.isArray(data.data)) {
        // Convert relative URLs to full URLs
        const images = data.data
          .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
          .map(img => ({
            ...img,
            imageUrl: img.imageUrl.startsWith('http') 
              ? img.imageUrl 
              : getFileUrl(img.imageUrl.startsWith('uploads/') ? img.imageUrl : `uploads/${img.imageUrl}`)
          }));
        setGalleryImages(images);
      }
    } catch (error) {
      console.error('Failed to fetch gallery images:', error);
    } finally {
      setGalleryLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLocationChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      location: { ...prev.location, [name]: value }
    }));
  };

  const handleSocialChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      socialLinks: { ...prev.socialLinks, [name]: value }
    }));
  };

  const handleAddBenefit = () => {
    if (newBenefit.trim()) {
      setFormData(prev => ({
        ...prev,
        benefits: [...prev.benefits, newBenefit.trim()]
      }));
      setNewBenefit('');
    }
  };

  const handleRemoveBenefit = (index) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index)
    }));
  };

  const handleAddSpecialty = (specialty) => {
    if (specialty.trim() && !formData.specialties.includes(specialty.trim())) {
      setFormData(prev => ({
        ...prev,
        specialties: [...prev.specialties, specialty.trim()]
      }));
    }
  };

  const handleRemoveSpecialty = (index) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.filter((_, i) => i !== index)
    }));
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (!company || !company._id) {
      toast.error('Company not found. Please create a company first.');
      return;
    }

    setUploadingPhotos(true);
    try {
      for (const file of files) {
        const formDataFile = new FormData();
        formDataFile.append('image', file); // Changed from 'photo' to 'image'

        await API.post(`/gallery/upload/${company._id}`, formDataFile, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success('Photos uploaded successfully');
      // Refresh gallery images after upload
      await fetchGalleryImages(company._id);
    } catch (error) {
      toast.error('Failed to upload photos');
      console.error(error);
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      const response = await API.delete(`/gallery/${photoId}`);
      setGalleryImages(prev => prev.filter(img => img._id !== photoId));
      toast.success('Photo deleted successfully');
      console.log('✅ Photo deleted:', photoId);
    } catch (error) {
      console.error('❌ Delete error:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to delete photo';
      toast.error(errorMsg);
    }
  };

  const handleSave = async () => {
    try {
      const { data } = await API.put(`/companies/${company._id}`, formData);
      setCompany(data);
      setEditing(false);
      toast.success('Company updated successfully');
    } catch (error) {
      toast.error('Failed to update company');
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">No Company Found</h1>
          <p className="text-gray-600 mb-6">You need to create a company profile first</p>
          <button
            onClick={() => navigate('/create-company')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium"
          >
            Create Company
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Hero Header */}
      <div className="relative bg-gradient-to-br from-[#00c2ab] to-[#009688] pt-16 pb-28 overflow-hidden">
        {/* Abstract Background Elements */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 rounded-full bg-white opacity-10 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-72 h-72 rounded-full bg-white opacity-10 blur-2xl"></div>
        
        <div className="container mx-auto px-4 relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-center md:text-left">
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-2 tracking-tight">Manage Company</h1>
            <p className="text-lg text-teal-50 font-medium opacity-90">{company.name}</p>
          </div>
          <button
            onClick={() => setEditing(!editing)}
            className={`flex items-center gap-2 px-8 py-3 rounded-xl font-bold shadow-lg transition-all duration-300 ${
              editing
                ? 'bg-white text-red-500 hover:bg-gray-50 hover:shadow-xl hover:-translate-y-0.5'
                : 'bg-white text-[#00c2ab] hover:bg-gray-50 hover:shadow-xl hover:-translate-y-0.5'
            }`}
          >
            {editing ? (
              <>
                <FaTimes className="text-xl" /> Cancel Editing
              </>
            ) : (
              <>
                <FaEdit className="text-xl" /> Edit Profile
              </>
            )}
          </button>
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-12 relative z-20 max-w-5xl">
        {/* Tabs */}
        <div className="flex flex-wrap gap-3 mb-8 justify-center md:justify-start">
          {['basic', 'gallery', 'benefits', 'location', 'social'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 font-bold rounded-xl transition-all duration-300 shadow-sm border ${
                activeTab === tab
                  ? 'bg-gradient-to-r from-[#00c2ab] to-[#009688] text-white border-transparent shadow-teal-500/30'
                  : 'bg-white text-gray-600 hover:text-[#00c2ab] hover:bg-gray-50 border-gray-200'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab Content Container */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 md:p-10 transition-all duration-300 hover:shadow-2xl">
          {/* Basic Information */}
          {activeTab === 'basic' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Company Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={!editing}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Website</label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    disabled={!editing}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed bg-white"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Industry</label>
                  <input
                    type="text"
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    disabled={!editing}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Company Size</label>
                  <select
                    name="companySize"
                    value={formData.companySize}
                    onChange={handleInputChange}
                    disabled={!editing}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed bg-white appearance-none"
                  >
                    <option value="">Select size</option>
                    <option value="1-10">1-10</option>
                    <option value="11-50">11-50</option>
                    <option value="51-200">51-200</option>
                    <option value="201-500">201-500</option>
                    <option value="501-1000">501-1000</option>
                    <option value="1000+">1000+</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Founded Year</label>
                  <input
                    type="number"
                    name="founded"
                    value={formData.founded}
                    onChange={handleInputChange}
                    disabled={!editing}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed bg-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  disabled={!editing}
                  rows="5"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed bg-white resize-none"
                />
              </div>

              {editing && (
                <div className="flex justify-end pt-4">
                  <button
                    onClick={handleSave}
                    className="px-8 py-3 bg-gradient-to-r from-[#00c2ab] to-[#009688] text-white rounded-xl font-bold text-lg hover:shadow-[0_8px_25px_rgba(0,194,171,0.4)] transform hover:-translate-y-1 transition-all duration-300 flex items-center gap-2"
                  >
                    <FaSave className="text-xl" /> Save Changes
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Gallery */}
          {activeTab === 'gallery' && (
            <div className="space-y-8 animate-fadeIn">
              {editing && (
                <div className="border-2 border-dashed border-[#00c2ab]/50 bg-teal-50/30 rounded-2xl p-10 text-center transition-all duration-300 hover:bg-teal-50/60">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    disabled={uploadingPhotos}
                    className="hidden"
                    id="photoUpload"
                  />
                  <label
                    htmlFor="photoUpload"
                    className="cursor-pointer flex flex-col items-center gap-3"
                  >
                    <div className="p-4 bg-white rounded-full shadow-sm text-[#00c2ab] mb-2">
                      <FaImage className="text-4xl" />
                    </div>
                    <span className="text-xl font-bold text-gray-800">
                      {uploadingPhotos ? 'Uploading...' : 'Click to upload photos'}
                    </span>
                    <span className="text-sm font-medium text-gray-500">
                      Upload high-quality images of your workspace and team
                    </span>
                  </label>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {galleryLoading ? (
                  <div className="col-span-full flex flex-col items-center justify-center py-12 text-[#00c2ab]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00c2ab] mb-4"></div>
                    <p className="font-medium">Loading gallery...</p>
                  </div>
                ) : galleryImages && galleryImages.length > 0 ? (
                  galleryImages.map((image) => (
                    <div key={image._id} className="relative group rounded-2xl overflow-hidden shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                      <img
                        src={image.imageUrl}
                        alt={image.title || 'Company Photo'}
                        className="w-full h-56 object-cover"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                        }}
                      />
                      {editing && (
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-sm">
                          <button
                            onClick={() => handleDeletePhoto(image._id)}
                            className="bg-red-500 hover:bg-red-600 text-white p-3 rounded-full shadow-lg transform hover:scale-110 transition-all"
                          >
                            <FaTrash className="text-lg" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="col-span-full text-center py-12">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 mb-4 text-gray-400">
                      <FaImage className="text-3xl" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-700 mb-2">No photos yet</h3>
                    <p className="text-gray-500">Upload photos to show candidates what it's like to work here.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Benefits */}
          {activeTab === 'benefits' && (
            <div className="space-y-8 animate-fadeIn">
              {editing && (
                <div className="flex flex-col sm:flex-row gap-4 bg-gray-50 p-6 rounded-2xl border border-gray-100">
                  <div className="flex-1">
                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Add a new benefit</label>
                    <input
                      type="text"
                      value={newBenefit}
                      onChange={(e) => setNewBenefit(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddBenefit()}
                      placeholder="e.g., Remote Work, Health Insurance"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm bg-white"
                    />
                  </div>
                  <div className="sm:self-end">
                    <button
                      onClick={handleAddBenefit}
                      className="w-full sm:w-auto px-8 py-3 bg-[#00c2ab] hover:bg-[#009688] text-white rounded-xl font-bold shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      <FaPlus /> Add Benefit
                    </button>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-4">
                {formData.benefits.length > 0 ? (
                  formData.benefits.map((benefit, idx) => (
                    <div
                      key={idx}
                      className="bg-teal-50 border border-teal-100 text-teal-800 rounded-xl px-5 py-3 flex items-center gap-3 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <FaCheckCircle className="text-[#00c2ab]" />
                      <span className="font-bold">{benefit}</span>
                      {editing && (
                        <button
                          onClick={() => handleRemoveBenefit(idx)}
                          className="ml-2 text-teal-600 hover:text-red-500 transition-colors p-1"
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 italic py-4">No benefits added yet.</p>
                )}
              </div>
              
              {editing && (
                <div className="flex justify-end pt-4 border-t border-gray-100">
                  <button
                    onClick={handleSave}
                    className="px-8 py-3 bg-gradient-to-r from-[#00c2ab] to-[#009688] text-white rounded-xl font-bold text-lg hover:shadow-[0_8px_25px_rgba(0,194,171,0.4)] transform hover:-translate-y-1 transition-all duration-300 flex items-center gap-2"
                  >
                    <FaSave className="text-xl" /> Save Changes
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Location */}
          {activeTab === 'location' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.location.address}
                    onChange={handleLocationChange}
                    disabled={!editing}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.location.city}
                    onChange={handleLocationChange}
                    disabled={!editing}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed bg-white"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">State</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.location.state}
                    onChange={handleLocationChange}
                    disabled={!editing}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Country</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.location.country}
                    onChange={handleLocationChange}
                    disabled={!editing}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed bg-white"
                  />
                </div>
              </div>

              {editing && (
                <div className="flex justify-end pt-4">
                  <button
                    onClick={handleSave}
                    className="px-8 py-3 bg-gradient-to-r from-[#00c2ab] to-[#009688] text-white rounded-xl font-bold text-lg hover:shadow-[0_8px_25px_rgba(0,194,171,0.4)] transform hover:-translate-y-1 transition-all duration-300 flex items-center gap-2"
                  >
                    <FaSave className="text-xl" /> Save Changes
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Social Links */}
          {activeTab === 'social' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide flex items-center gap-2">
                    <FaLinkedin className="text-blue-600 text-lg" /> LinkedIn
                  </label>
                  <input
                    type="url"
                    name="linkedin"
                    value={formData.socialLinks.linkedin}
                    onChange={handleSocialChange}
                    disabled={!editing}
                    placeholder="https://linkedin.com/company/..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide flex items-center gap-2">
                    <FaTwitter className="text-blue-400 text-lg" /> Twitter
                  </label>
                  <input
                    type="url"
                    name="twitter"
                    value={formData.socialLinks.twitter}
                    onChange={handleSocialChange}
                    disabled={!editing}
                    placeholder="https://twitter.com/..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed bg-white"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide flex items-center gap-2">
                    <FaFacebook className="text-blue-600 text-lg" /> Facebook
                  </label>
                  <input
                    type="url"
                    name="facebook"
                    value={formData.socialLinks.facebook}
                    onChange={handleSocialChange}
                    disabled={!editing}
                    placeholder="https://facebook.com/..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide flex items-center gap-2">
                    <FaInstagram className="text-pink-600 text-lg" /> Instagram
                  </label>
                  <input
                    type="url"
                    name="instagram"
                    value={formData.socialLinks.instagram}
                    onChange={handleSocialChange}
                    disabled={!editing}
                    placeholder="https://instagram.com/..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed bg-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide flex items-center gap-2">
                  <FaYoutube className="text-red-600 text-lg" /> YouTube
                </label>
                <input
                  type="url"
                  name="youtube"
                  value={formData.socialLinks.youtube}
                  onChange={handleSocialChange}
                  disabled={!editing}
                  placeholder="https://youtube.com/..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed bg-white"
                />
              </div>

              {editing && (
                <div className="flex justify-end pt-4">
                  <button
                    onClick={handleSave}
                    className="px-8 py-3 bg-gradient-to-r from-[#00c2ab] to-[#009688] text-white rounded-xl font-bold text-lg hover:shadow-[0_8px_25px_rgba(0,194,171,0.4)] transform hover:-translate-y-1 transition-all duration-300 flex items-center gap-2"
                  >
                    <FaSave className="text-xl" /> Save Changes
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageCompany;
